import { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import backendCall from './AxiosService';
import type { ApiResponse, ApiError } from '@/types';

export interface ServiceConfig {
    timeout?: number;
    retries?: number;
    retryDelay?: number;
    enableLogging?: boolean;
}

export interface RequestOptions {
    timeout?: number;
    retries?: number;
    skipErrorHandling?: boolean;
    transformResponse?: boolean;
}

export class ServiceError extends Error {
    public readonly status: number;
    public readonly code?: string;
    public readonly details?: any;
    public readonly timestamp: string;

    constructor(message: string, status: number = 500, code?: string, details?: any) {
        super(message);
        this.name = 'ServiceError';
        this.status = status;
        this.code = code;
        this.details = details;
        this.timestamp = new Date().toISOString();
    }
}

export abstract class BaseService {
    protected readonly client: AxiosInstance;
    protected readonly config: ServiceConfig;
    protected readonly serviceName: string;

    constructor(serviceName: string, config: ServiceConfig = {}) {
        this.client = backendCall;
        this.serviceName = serviceName;
        this.config = {
            timeout: 30000,
            retries: 3,
            retryDelay: 1000,
            enableLogging: true,
            ...config
        };
    }

    /**
     * Standardized GET request
     */
    protected async get<T>(
        url: string, 
        options: RequestOptions = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>('GET', url, undefined, options);
    }

    /**
     * Standardized POST request
     */
    protected async post<T>(
        url: string, 
        data?: any, 
        options: RequestOptions = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>('POST', url, data, options);
    }

    /**
     * Standardized PUT request
     */
    protected async put<T>(
        url: string, 
        data?: any, 
        options: RequestOptions = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>('PUT', url, data, options);
    }

    /**
     * Standardized DELETE request
     */
    protected async delete<T>(
        url: string, 
        options: RequestOptions = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>('DELETE', url, undefined, options);
    }

    /**
     * Core request method with retry logic and error handling
     */
    private async request<T>(
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        url: string,
        data?: any,
        options: RequestOptions = {}
    ): Promise<ApiResponse<T>> {
        const requestConfig = {
            method: method.toLowerCase(),
            url,
            data,
            timeout: options.timeout || this.config.timeout,
        };

        const maxRetries = options.retries ?? this.config.retries ?? 3;
        let lastError: any;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                this.log(`[${this.serviceName}] ${method} ${url}${attempt > 0 ? ` (attempt ${attempt + 1})` : ''}`);
                
                const response: AxiosResponse = await this.client.request(requestConfig);
                
                // Transform response to standardized format
                return this.transformResponse<T>(response, options.transformResponse !== false);
                
            } catch (error) {
                lastError = error;
                
                // Don't retry on certain error types
                if (!this.shouldRetry(error, attempt, maxRetries)) {
                    break;
                }
                
                // Wait before retry
                if (attempt < maxRetries) {
                    await this.delay(this.config.retryDelay! * (attempt + 1));
                }
            }
        }

        // Handle final error
        if (!options.skipErrorHandling) {
            throw this.handleError(lastError, method, url);
        }
        
        throw lastError;
    }

    /**
     * Transform axios response to standardized ApiResponse format
     */
    private transformResponse<T>(response: AxiosResponse, transform: boolean = true): ApiResponse<T> {
        if (!transform) {
            return response.data;
        }

        // Handle different response formats from backend
        const data = response.data;
        
        // If already in ApiResponse format
        if (data && typeof data === 'object' && 'success' in data) {
            return data as ApiResponse<T>;
        }
        
        // If backend returns { data: T } format
        if (data && typeof data === 'object' && 'data' in data && !('success' in data)) {
            return {
                success: true,
                data: data.data as T
            };
        }
        
        // If backend returns direct data
        return {
            success: true,
            data: data as T
        };
    }

    /**
     * Standardized error handling
     */
    private handleError(error: any, method: string, url: string): ServiceError {
        this.log(`[${this.serviceName}] Error in ${method} ${url}:`, error);

        if (error instanceof AxiosError) {
            const status = error.response?.status || 500;
            const responseData = error.response?.data;
            
            // Extract error message from different response formats
            let message = error.message;
            let code: string | undefined;
            let details: any;

            if (responseData) {
                if (responseData.message) {
                    message = responseData.message;
                }
                if (responseData.error?.message) {
                    message = responseData.error.message;
                }
                if (responseData.errors && Array.isArray(responseData.errors)) {
                    message = responseData.errors.join(', ');
                }
                code = responseData.code || responseData.error?.code;
                details = responseData.details || responseData.error?.details;
            }

            return new ServiceError(message, status, code, details);
        }

        // Network or other errors
        return new ServiceError(
            error.message || 'An unexpected error occurred',
            0,
            'NETWORK_ERROR',
            error
        );
    }

    /**
     * Determine if request should be retried
     */
    private shouldRetry(error: any, attempt: number, maxRetries: number): boolean {
        if (attempt >= maxRetries) {
            return false;
        }

        if (error instanceof AxiosError) {
            const status = error.response?.status;
            
            // Don't retry client errors (4xx) except for specific cases
            if (status && status >= 400 && status < 500) {
                // Retry on rate limiting and authentication issues
                return status === 429 || status === 401;
            }
            
            // Retry on server errors (5xx) and network errors
            return !status || status >= 500;
        }

        // Retry on network errors
        return true;
    }

    /**
     * Delay helper for retries
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Logging helper
     */
    protected log(message: string, ...args: any[]): void {
        if (this.config.enableLogging) {
            console.log(message, ...args);
        }
    }

    /**
     * Helper method to build URLs with query parameters
     */
    protected buildUrl(path: string, params?: Record<string, any>): string {
        if (!params) return path;
        
        const queryString = Object.entries(params)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');
            
        return queryString ? `${path}?${queryString}` : path;
    }

    /**
     * Helper to validate required parameters
     */
    protected validateRequired(params: Record<string, any>): void {
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null || value === '') {
                throw new ServiceError(`Missing required parameter: ${key}`, 400, 'VALIDATION_ERROR');
            }
        }
    }

    /**
     * Helper to validate parameter types
     */
    protected validateTypes(params: Record<string, any>, types: Record<string, string>): void {
        for (const [key, expectedType] of Object.entries(types)) {
            const value = params[key];
            if (value !== undefined && typeof value !== expectedType) {
                throw new ServiceError(
                    `Invalid type for parameter ${key}: expected ${expectedType}, got ${typeof value}`,
                    400,
                    'TYPE_ERROR'
                );
            }
        }
    }
}

/**
 * Factory function to create service instances with shared configuration
 */
export function createService<T extends BaseService>(
    ServiceClass: new (config?: ServiceConfig) => T,
    config?: ServiceConfig
): T {
    return new ServiceClass(config);
}